/**
 * AI 对话过程控制：停止丢弃 / 回退 / 重试 的确认文案与会话动作状态。
 */
import { ElMessageBox } from 'element-plus';
import type { AiTaskKind } from '@/stores/useAiTaskQueueStore';

export const AI_SESSION_CONFIRM_Z_INDEX = 5001;
export const AI_SESSION_CONFIRM_MODAL_CLASS = 'ai-session-control-confirm-overlay';

export type AiSessionAction = {
  kind: AiTaskKind;
  label: string;
  /** 递增代数；停止后晚到结果用 generation 比对丢弃 */
  generation: number;
  discarded: boolean;
  /** 对应会话里展示「回退/重试」的助手消息 */
  messageId?: string;
  /** 丢弃已写入项目侧的结果，恢复到会话前状态 */
  discard: () => void | Promise<void>;
  /** 放弃上次结果并重新执行 */
  retry: () => Promise<void>;
};

/** 终止后待确认是否回退的项目侧修改 */
export type PendingTerminatedRevert = {
  label: string;
  discard: () => void | Promise<void>;
};

let generationSeq = 0;

export function nextAiSessionGeneration(): number {
  generationSeq += 1;
  return generationSeq;
}

export function isAiSessionActionActive(
  action: AiSessionAction | null | undefined,
  generation: number,
): boolean {
  return !!action && action.generation === generation && !action.discarded;
}

/** 回退确认：放弃此次编辑并回退到会话之前的状态 */
export async function confirmAiSessionRollback(label?: string): Promise<boolean> {
  const name = (label || '本次 AI 结果').trim();
  try {
    await ElMessageBox.confirm(
      `将放弃「${name}」产生的编辑，并回退到发起会话之前的状态。是否继续？`,
      '回退确认',
      {
        type: 'warning',
        confirmButtonText: '放弃并回退',
        cancelButtonText: '取消',
        distinguishCancelAndClose: true,
        zIndex: AI_SESSION_CONFIRM_Z_INDEX,
        modalClass: AI_SESSION_CONFIRM_MODAL_CLASS,
      },
    );
    return true;
  } catch {
    return false;
  }
}

/** 重试确认：放弃上一次会话生成的结果重新执行 */
export async function confirmAiSessionRetry(label?: string): Promise<boolean> {
  const name = (label || '本次 AI 任务').trim();
  try {
    await ElMessageBox.confirm(
      `将放弃「${name}」上一次生成的结果，并重新执行。是否继续？`,
      '重试确认',
      {
        type: 'warning',
        confirmButtonText: '放弃并重试',
        cancelButtonText: '取消',
        distinguishCancelAndClose: true,
        zIndex: AI_SESSION_CONFIRM_Z_INDEX,
        modalClass: AI_SESSION_CONFIRM_MODAL_CLASS,
      },
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * 终止后再次发送：若上次任务可能已改项目内容，询问是否先回退。
 * @returns 'revert' | 'keep' | 'abort'
 */
export async function confirmPendingRevertBeforeSend(
  label?: string,
): Promise<'revert' | 'keep' | 'abort'> {
  const name = (label || '上次任务').trim();
  try {
    await ElMessageBox.confirm(
      `「${name}」可能已修改项目内容。是否先回退这些修改再继续发送？`,
      '回退确认',
      {
        type: 'warning',
        confirmButtonText: '回退修改',
        cancelButtonText: '保留修改',
        distinguishCancelAndClose: true,
        zIndex: AI_SESSION_CONFIRM_Z_INDEX,
        modalClass: AI_SESSION_CONFIRM_MODAL_CLASS,
      },
    );
    return 'revert';
  } catch (e) {
    if (e === 'cancel') return 'keep';
    return 'abort';
  }
}

/** 停止时提示（软终止：不自动回退，排队已暂停） */
export function stoppedDiscardMessage(label?: string): string {
  const name = (label || '任务').trim();
  return `已停止「${name}」，后续排队任务已暂停。输入内容已恢复，可编辑后重新发送。`;
}
